// Load the editor dynamically to avoid bundling quill-related CJS code into main chunk
import katex from 'katex'
import 'katex/dist/katex.min.css'
import renderMathInElement from 'katex/contrib/auto-render'
import { useEffect, useRef } from 'react'
import { Button, Modal, } from "flowbite-react";
import React, { useState } from "react";
import DOMPurify from 'dompurify';
import { useSchoolExamQuestionAdd } from '../../react-query/queries';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { IQuestion } from '../../types/question';
import { useNavigate, useParams } from 'react-router-dom';

const ExamQuestionsInitial = ({ questions, onProceedToMeta }: { questions: IQuestion[], onProceedToMeta?: (qs: IQuestion[]) => void }) => {
    const { token } = useAuth();
    const { examId } = useParams();
    const navigate = useNavigate();
    const questionsArray: any[] = [];
    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        // After questions render, run KaTeX auto-render to catch any inline/display math we didn't replace
        try {
            if (containerRef.current) {
                renderMathInElement(containerRef.current, {
                    // default delimiters for inline and display math
                    delimiters: [
                        { left: '$$', right: '$$', display: true },
                        { left: '$', right: '$', display: false },
                        { left: '\\(', right: '\\)', display: false },
                        { left: '\\[', right: '\\]', display: true }
                    ],
                    // do not throw on errors
                    throwOnError: false,
                })
            }
        } catch (e) {
            // ignore auto-render errors
            console.warn('KaTeX auto-render failed', e)
        }
    }, [questions])
    const [openModal, setOpenModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<IQuestion>({
        question: '',
        choice_a: '',
        choice_b: '',
        choice_c: '',
        choice_d: '',
        answer: '',
    })

    const [EditorComp, setEditorComp] = useState<any>(null);

    // Simple fallback editor used when dynamic import of the rich editor fails
    const SimpleFallbackEditor = ({ initialValue, onChange }: { initialValue?: string, onChange?: (val: string) => void }) => {
        const [val, setVal] = useState<string>(initialValue || '');
        useEffect(() => { setVal(initialValue || '') }, [initialValue]);
        return (
            <textarea
                value={val}
                onChange={(e) => { setVal(e.target.value); onChange && onChange(e.target.value) }}
                className="w-full min-h-[120px] p-2 border rounded" />
        )
    }

    // Temporary image preview/file selected in the edit modal. Not uploaded yet.
    const [selectedImagePreview, setSelectedImagePreview] = useState<string | null>(null)
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)

    const [selectedOption, setSelectedOption] = useState<string>('');
    const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>()
    const [_isQuestionAddingState, _setIsQuestionAddingState] = useState(false)
    const { mutateAsync: schoolExamQuestionAdd } = useSchoolExamQuestionAdd()

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const mod = await import('quill-editor-math');
                if (!mounted) return;
                const Comp = (mod && (mod.default || mod));
                setEditorComp(() => Comp);
                try { await import('quill-editor-math/dist/index.css'); } catch (e) { }
            } catch (e) {
                // If dynamic import fails (commonly in production builds due to bundling/CJS issues),
                // log the error and fall back to the simple textarea editor so editing still works.
                // eslint-disable-next-line no-console
                console.error('Failed to load quill-editor-math dynamic import:', e)
                if (!mounted) return
                setEditorComp(() => SimpleFallbackEditor)
            }
        })();
        return () => { mounted = false; };
    }, [])

    const addExamQuestions = async (event: React.FormEvent) => {
        _setIsQuestionAddingState(true)
        event.preventDefault();

        // If parent provided an onProceedToMeta handler, forward the questions to the parent
            if (typeof onProceedToMeta === 'function') {
            onProceedToMeta(questions)
            _setIsQuestionAddingState(false)
            return
        }

        const new_exam = schoolExamQuestionAdd({ toBeValidated: token, data: questions, exam_id: examId! }, {
            onSuccess(data) {
                if(data['stat'] == 'success'){
                    navigate(`/exams/exam-detail/${examId}`)
                }
                _setIsQuestionAddingState(false)
            }
        })

        if (!new_exam) {
        }
    }

    const changeTextColor = () => {
        setIsOptionSelected(true);
    };

    const questionEdit = (ind: number) => {
        setSelectedQuestion(questions[ind])
        setSelectedOption(questions[ind].answer)
        // setup image preview/file if the question already has an image or a pending file
        const q = questions[ind] as any
        setSelectedImagePreview(q?.image || null)
        setSelectedImageFile(q?.pendingImageFile || null)
        setSelectedIndex(ind)
        setOpenModal(true)
    }

    // Compress an image File to target size (kb) using canvas. Returns a File.
    const compressImageFile = async (file: File, maxKB = 40): Promise<File> => {
        const maxBytes = maxKB * 1024
        // if already small enough, return original
        if (file.size <= maxBytes) return file

        const img = document.createElement('img')
        const objectUrl = URL.createObjectURL(file)
        img.src = objectUrl

        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve()
            img.onerror = () => reject(new Error('Failed to load image for compression'))
        })

        const canvas = document.createElement('canvas')
        let [width, height] = [img.naturalWidth, img.naturalHeight]
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0, width, height)

        const toBlob = (quality: number, mime = 'image/jpeg', w = width, h = height) => {
            return new Promise<Blob | null>((res) => {
                const c = document.createElement('canvas')
                c.width = w
                c.height = h
                const cctx = c.getContext('2d')!
                cctx.drawImage(img, 0, 0, w, h)
                c.toBlob((b) => res(b), mime, quality)
            })
        }

        let quality = 0.92
        let attempt = 0
        let blob: Blob | null = null
        let curW = width
        let curH = height

        while (attempt < 12) {
            blob = await toBlob(quality, 'image/jpeg', curW, curH)
            if (blob && blob.size <= maxBytes) break
            // reduce quality first
            quality = Math.max(0.25, quality - 0.12)
            attempt++
            if (attempt % 4 === 0) {
                // after a few tries, scale down dimensions
                curW = Math.max(200, Math.floor(curW * 0.85))
                curH = Math.max(200, Math.floor(curH * 0.85))
            }
        }

        URL.revokeObjectURL(objectUrl)
        if (blob) {
            try {
                const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })
                return newFile
            } catch (e) {
                // older browsers may not support File constructor
                return new File([blob], file.name, { type: blob.type })
            }
        }
        return file
    }


    const questionEditChange = (event: string, name: string) => {
        setSelectedQuestion((prevFormData) => ({
            ...prevFormData,
            [name]: event,
        }));
    };

    const saveQuestionEdit = () => {
        questions[selectedIndex!].question = selectedQuestion.question
        // Persist note field back to the questions array
        questions[selectedIndex!].note = selectedQuestion.note
        questions[selectedIndex!].choice_a = selectedQuestion.choice_a
        questions[selectedIndex!].choice_b = selectedQuestion.choice_b
        questions[selectedIndex!].choice_c = selectedQuestion.choice_c
        questions[selectedIndex!].choice_d = selectedQuestion.choice_d
        // Persist selected answer choice back to the questions array
        questions[selectedIndex!].answer = selectedOption
        // store pending image file (will be uploaded later by parent save handler)
        if (selectedImageFile) {
            ;(questions[selectedIndex!] as any).pendingImageFile = selectedImageFile
            // store a pending preview URL (only used for preview, not to indicate saved image)
            ;(questions[selectedIndex!] as any).pendingImagePreview = selectedImagePreview || ''
        } else {
            // if no file selected but preview was cleared, remove pendingImage
            if (!selectedImagePreview) {
                delete (questions[selectedIndex!] as any).pendingImageFile
                delete (questions[selectedIndex!] as any).pendingImagePreview
            }
        }
        setOpenModal(false)
    }


    questions.forEach((element, key) => {
        questionsArray.push(
            <div key={key} className="container mx-auto px-10">
                <div className='pb-3 flex items-center gap-2'>
                    <span className="bg-blue-200 text-blue-950 text-xs font-medium me-2 px-2.5 py-0.5 rounded-lg dark:bg-purple-900 dark:text-purple-300">Question <span className="font-bold">{key + 1} / {questions.length}</span></span>
                    {element.image && (
                        <span className="bg-yellow-200 text-yellow-900 text-xs font-medium px-2.5 py-0.5 rounded-lg dark:bg-yellow-900 dark:text-yellow-300">Contains Image</span>
                    )}
                </div>
                {element.note && (
                    <div className="italic text-gray-500 dark:text-gray-400 mb-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.note) }} />
                )}
                {/* Helper to render LaTeX inside sanitized HTML */}
                {/* Render display math $$...$$ and inline $...$ */}
                
                
                {/* Show preview only for a newly selected image (pendingImagePreview). Do not preview when element.image exists unless it is a pending preview URL. */}
                
                <div className="font-bold" dangerouslySetInnerHTML={{ __html: (() => { try { const s = DOMPurify.sanitize(element.question || ''); return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m } }) } catch (e) { return String(element.question || '') } })() }} /><br />
                {((element as any).pendingImagePreview) ? (
                    <div className="mb-3"><img src={String((element as any).pendingImagePreview)} alt={`preview-${key}`} className="max-h-48 mb-2" /></div>
                ) : null}
                <br />
                <div className="flex flex-row w-full gap-5">
                    <div className={element.answer == 'A' ? " basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-green-400 text-green-950 dark:bg-green-400 dark:text-green-950" : "basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300"}>
                        <div className='grid grid-cols-12'>
                            <span className="col-span-1 font-bold text-md justify-center items-center">A.</span>
                            <div className='col-span-11' dangerouslySetInnerHTML={{ __html: (() => { try { const s = DOMPurify.sanitize(element.choice_a || ''); return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m } }) } catch (e) { return String(element.choice_a || '') } })() }} />
                        </div>

                    </div>
                    <div className={element.answer == 'B' ? " basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-green-400 text-green-950 dark:bg-green-400 dark:text-green-950" : "basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300"}>
                        <div className='grid grid-cols-12'>
                            <span className="col-span-1 font-bold text-md">B.</span>
                            <div className='col-span-11' dangerouslySetInnerHTML={{ __html: (() => { try { const s = DOMPurify.sanitize(element.choice_b || ''); return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m } }) } catch (e) { return String(element.choice_b || '') } })() }} />
                        </div>
                    </div>
                </div>
                <br />
                <div className="flex flex-row w-full gap-5">
                    <div className={element.answer == 'C' ? " basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-green-400 text-green-950 dark:bg-green-400 dark:text-green-950" : "basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300"}>
                        <div className='grid grid-cols-12'>
                            <span className="col-span-1 font-bold text-md">C.</span>
                            <div className='col-span-11' dangerouslySetInnerHTML={{ __html: (() => { try { const s = DOMPurify.sanitize(element.choice_c || ''); return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m } }) } catch (e) { return String(element.choice_c || '') } })() }} />
                        </div>
                    </div>
                    <div className={element.answer == 'D' ? " basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-green-400 text-green-950 dark:bg-green-400 dark:text-green-950" : "basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300"}>
                        <div className='grid grid-cols-12'>
                            <span className="col-span-1 font-bold text-md">D.</span>
                            <div className='col-span-11' dangerouslySetInnerHTML={{ __html: (() => { try { const s = DOMPurify.sanitize(element.choice_d || ''); return s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m } }).replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => { try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m } }) } catch (e) { return String(element.choice_d || '') } })() }} />
                        </div>
                    </div>
                </div>
                <br />
                {element.answer && (
                    <div className="mt-2">
                        <span className="bg-green-200 text-green-950 text-xs font-medium me-2 px-2.5 py-0.5 rounded-lg dark:bg-green-900 dark:text-green-300">
                            Correct Answer: {element.answer}
                        </span>
                    </div>
                )}
                <div className="flex items-center justify-center space-x-3.5">
                    <button title="Edit button" onClick={() => questionEdit(key)} type="button" className="text-blue-700 border border-blue-700 hover:bg-blue-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:focus:ring-blue-800 dark:hover:bg-blue-500">
                        <svg
                            className="fill-current"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8.99981 14.8219C3.43106 14.8219 0.674805 9.50624 0.562305 9.28124C0.47793 9.11249 0.47793 8.88749 0.562305 8.71874C0.674805 8.49374 3.43106 3.20624 8.99981 3.20624C14.5686 3.20624 17.3248 8.49374 17.4373 8.71874C17.5217 8.88749 17.5217 9.11249 17.4373 9.28124C17.3248 9.50624 14.5686 14.8219 8.99981 14.8219ZM1.85605 8.99999C2.4748 10.0406 4.89356 13.5562 8.99981 13.5562C13.1061 13.5562 15.5248 10.0406 16.1436 8.99999C15.5248 7.95936 13.1061 4.44374 8.99981 4.44374C4.89356 4.44374 2.4748 7.95936 1.85605 8.99999Z"
                                fill=""
                            />
                            <path
                                d="M9 11.3906C7.67812 11.3906 6.60938 10.3219 6.60938 9C6.60938 7.67813 7.67812 6.60938 9 6.60938C10.3219 6.60938 11.3906 7.67813 11.3906 9C11.3906 10.3219 10.3219 11.3906 9 11.3906ZM9 7.875C8.38125 7.875 7.875 8.38125 7.875 9C7.875 9.61875 8.38125 10.125 9 10.125C9.61875 10.125 10.125 9.61875 10.125 9C10.125 8.38125 9.61875 7.875 9 7.875Z"
                                fill=""
                            />
                        </svg>
                    </button>
                    <button title="Delete button" type="button" className="text-blue-700 border border-blue-700 hover:bg-blue-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:focus:ring-blue-800 dark:hover:bg-blue-500">
                        <svg
                            className="fill-current"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M13.7535 2.47502H11.5879V1.9969C11.5879 1.15315 10.9129 0.478149 10.0691 0.478149H7.90352C7.05977 0.478149 6.38477 1.15315 6.38477 1.9969V2.47502H4.21914C3.40352 2.47502 2.72852 3.15002 2.72852 3.96565V4.8094C2.72852 5.42815 3.09414 5.9344 3.62852 6.1594L4.07852 15.4688C4.13477 16.6219 5.09102 17.5219 6.24414 17.5219H11.7004C12.8535 17.5219 13.8098 16.6219 13.866 15.4688L14.3441 6.13127C14.8785 5.90627 15.2441 5.3719 15.2441 4.78127V3.93752C15.2441 3.15002 14.5691 2.47502 13.7535 2.47502ZM7.67852 1.9969C7.67852 1.85627 7.79102 1.74377 7.93164 1.74377H10.0973C10.2379 1.74377 10.3504 1.85627 10.3504 1.9969V2.47502H7.70664V1.9969H7.67852ZM4.02227 3.96565C4.02227 3.85315 4.10664 3.74065 4.24727 3.74065H13.7535C13.866 3.74065 13.9785 3.82502 13.9785 3.96565V4.8094C13.9785 4.9219 13.8941 5.0344 13.7535 5.0344H4.24727C4.13477 5.0344 4.02227 4.95002 4.02227 4.8094V3.96565ZM11.7285 16.2563H6.27227C5.79414 16.2563 5.40039 15.8906 5.37227 15.3844L4.95039 6.2719H13.0785L12.6566 15.3844C12.6004 15.8625 12.2066 16.2563 11.7285 16.2563Z"
                                fill=""
                            />
                            <path
                                d="M9.00039 9.11255C8.66289 9.11255 8.35352 9.3938 8.35352 9.75942V13.3313C8.35352 13.6688 8.63477 13.9782 9.00039 13.9782C9.33789 13.9782 9.64727 13.6969 9.64727 13.3313V9.75942C9.64727 9.3938 9.33789 9.11255 9.00039 9.11255Z"
                                fill=""
                            />
                            <path
                                d="M11.2502 9.67504C10.8846 9.64692 10.6033 9.90004 10.5752 10.2657L10.4064 12.7407C10.3783 13.0782 10.6314 13.3875 10.9971 13.4157C11.0252 13.4157 11.0252 13.4157 11.0533 13.4157C11.3908 13.4157 11.6721 13.1625 11.6721 12.825L11.8408 10.35C11.8408 9.98442 11.5877 9.70317 11.2502 9.67504Z"
                                fill=""
                            />
                            <path
                                d="M6.72245 9.67504C6.38495 9.70317 6.1037 10.0125 6.13182 10.35L6.3287 12.825C6.35683 13.1625 6.63808 13.4157 6.94745 13.4157C6.97558 13.4157 6.97558 13.4157 7.0037 13.4157C7.3412 13.3875 7.62245 13.0782 7.59433 12.7407L7.39745 10.2657C7.39745 9.90004 7.08808 9.64692 6.72245 9.67504Z"
                                fill=""
                            />
                        </svg>
                    </button>
                </div>
                <br /><br />
            </div>)
    })
    return (
        <>
            <Modal size="4xl" className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/50 px-4 py-5' show={openModal} onClose={() => setOpenModal(false)}>
                <Modal.Header>{selectedIndex != null ? `Editing question number ${selectedIndex + 1}` : 'Editing question'}</Modal.Header>
                <Modal.Body>
                    <div className="space-y-6 h-100">
                        <span className="font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Question
                        </span>
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.question} onChange={(e: any) => { questionEditChange(e, 'question') }} /> : <div>Loading editor…</div>}<br /><br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice A
                        </span>
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.choice_a} onChange={(e: any) => { questionEditChange(e, 'choice_a') }} /> : <div>Loading editor…</div>}<br /><br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice B
                        </span>
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.choice_b} onChange={(e: any) => { questionEditChange(e, 'choice_b') }} /> : <div>Loading editor…</div>}<br /><br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice C
                        </span>
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.choice_c} onChange={(e: any) => { questionEditChange(e, 'choice_c') }} /> : <div>Loading editor…</div>}<br /><br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice D
                        </span>
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.choice_d} onChange={(e: any) => { questionEditChange(e, 'choice_d') }} /> : <div>Loading editor…</div>}<br /><br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Note (optional)
                        </span>
                        
                        {EditorComp ? <EditorComp initialValue={selectedQuestion?.note || ''} onChange={(e: any) => { questionEditChange(e, 'note') }} /> : <div>Loading editor…</div>}<br />
                        <div className='mt-3'>
                            <label className='block text-sm font-medium mb-1'>Image (optional)</label>
                            {selectedImageFile != null ? (
                                <img src={selectedImagePreview ?? undefined} alt="preview" className='max-h-40 mb-2' />
                            ) : null}
                            <input title="Upload an image (will be compressed to <40KB)" type="file" accept="image/*" onChange={async (e) => {
                                const f = e.target.files ? e.target.files[0] : null
                                if (!f) {
                                    setSelectedImageFile(null)
                                    setSelectedImagePreview(null)
                                    return
                                }
                                try {
                                    const compressed = await compressImageFile(f, 40)
                                    setSelectedImageFile(compressed)
                                    const url = URL.createObjectURL(compressed)
                                    setSelectedImagePreview(url)
                                    // immediately attach pending file and preview to the question so list shows preview
                                    if (selectedIndex != null && typeof selectedIndex === 'number') {
                                        try {
                                            ;(questions[selectedIndex] as any).pendingImageFile = compressed
                                            ;(questions[selectedIndex] as any).image = url
                                        } catch (e) {
                                            // ignore
                                        }
                                    }
                                } catch (err) {
                                    // fallback to original file
                                    setSelectedImageFile(f)
                                    const url = URL.createObjectURL(f)
                                    setSelectedImagePreview(url)
                                    console.log(selectedImagePreview)
                                    if (selectedIndex != null && typeof selectedIndex === 'number') {
                                        try {
                                            ;(questions[selectedIndex] as any).pendingImageFile = f
                                            ;(questions[selectedIndex] as any).image = url
                                        } catch (e) {
                                            // ignore
                                        }
                                    }
                                }
                            }} />
                        </div><br /><br />

                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Answer
                        </span>
                        <div className='columns-3'>
                            <div></div>
                            <select
                                title='Answer choice'
                                value={selectedOption}
                                onChange={(e) => {
                                    setSelectedOption(e.target.value);
                                    changeTextColor();
                                }}
                                className={`relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-12 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input ${isOptionSelected ? 'text-black dark:text-white' : ''
                                    }`}
                            >
                                <option value="A" className="text-body dark:text-bodydark">
                                    Choice A
                                </option>
                                <option value="B" className="text-body dark:text-bodydark">
                                    Choice B
                                </option>
                                <option value="C" className="text-body dark:text-bodydark">
                                    Choice C
                                </option>
                                <option value="D" className="text-body dark:text-bodydark">
                                    Choice D
                                </option>
                            </select>
                        </div>

                        <br /><br /><br /><br /><br /><br />

                        



                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => saveQuestionEdit()}>Save</Button>
                    <Button color="gray" onClick={() => setOpenModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>


            <div ref={containerRef}>
                {questionsArray}
            </div>
        </>
    )
}

export default ExamQuestionsInitial
